import { validatePostSessionProvenance } from "./postSessionProvenance";

export const POST_COMPLETION_RECONCILIATION_DELAYS_MS = Object.freeze([
  1500,
  4500,
  9500,
  19000,
]);

const KNOWN_AUTOMATIC_EMAIL_STATUSES = new Set([
  "not_started",
  "not_requested",
  "pending",
  "requested",
  "claiming",
  "dispatching",
  "queued",
  "queue_accepted",
  "sent",
  "accepted",
  "provider_accepted",
  "delivered",
  "failed",
  "ambiguous",
  "bounced",
  "suppressed",
]);

const KNOWN_REMINDER_STATUSES = new Set([
  "not_started",
  "not_scheduled",
  "scheduled",
  "pending",
  "dispatching",
  "queued",
  "queue_accepted",
  "sent",
  "provider_accepted",
  "delivered",
  "failed",
  "ambiguous",
  "cancelled",
  "canceled",
  "suppressed",
]);

function statusToken(value = "") {
  return String(value || "").trim().toLowerCase().replace(/[\s-]+/g, "_");
}

export function normalizePostCompletionReconciliationScope({
  authScopeKey = "",
  orgId = "",
  sessionId = "",
} = {}) {
  return {
    authScopeKey: String(authScopeKey || "").trim(),
    orgId: String(orgId || "").trim(),
    sessionId: String(sessionId || "").trim(),
  };
}

export function postCompletionSessionDocumentPath(scope = {}) {
  const normalized = normalizePostCompletionReconciliationScope(scope);
  return normalized.orgId && normalized.sessionId
    ? `orgs/${normalized.orgId}/sessions/${normalized.sessionId}`
    : "";
}

export function normalizePostCompletionReconciliationSchedule(value) {
  const supplied = Array.isArray(value) ? value : [];
  const normalized = supplied
    .map(Number)
    .filter((delay) => Number.isFinite(delay) && delay >= 0)
    .sort((left, right) => left - right)
    .slice(0, POST_COMPLETION_RECONCILIATION_DELAYS_MS.length);
  return normalized.length
    ? normalized
    : [...POST_COMPLETION_RECONCILIATION_DELAYS_MS];
}

export function validatePostCompletionReconciliationRecord(
  record,
  scope = {}
) {
  const normalized = normalizePostCompletionReconciliationScope(scope);
  if (!record || typeof record !== "object") {
    return { valid: false, reason: "document_missing" };
  }
  if (!normalized.authScopeKey || !normalized.orgId || !normalized.sessionId) {
    return { valid: false, reason: "scope_incomplete" };
  }
  if (String(record.id || "").trim() !== normalized.sessionId) {
    return { valid: false, reason: "session_id_mismatch" };
  }
  if (String(record.orgId || "").trim() !== normalized.orgId) {
    return { valid: false, reason: "organisation_mismatch" };
  }
  if (String(record.userId || "").trim() !== normalized.authScopeKey) {
    return { valid: false, reason: "owner_mismatch" };
  }
  return { valid: true, reason: "trusted_scope" };
}

export function isCanonicalPostSessionStateVerified(record = null) {
  if (!record || typeof record !== "object") return false;
  const provenance = validatePostSessionProvenance(record);
  const continuation = record.postSessionContinuation || {};
  const automaticStatus = statusToken(continuation.automaticEmail?.status);
  const reminderStatus = statusToken(continuation.reminder?.status);
  return Boolean(
    provenance.trustedFinalSummary === true &&
    provenance.automaticEmailBindingValid === true &&
    provenance.reminderBindingValid === true &&
    KNOWN_AUTOMATIC_EMAIL_STATUSES.has(automaticStatus) &&
    KNOWN_REMINDER_STATUSES.has(reminderStatus)
  );
}

export function createPostCompletionReconciliationController({
  schedule = POST_COMPLETION_RECONCILIATION_DELAYS_MS,
  readRecord,
  applyRecord,
  getCurrentRecord = () => null,
  isVerified = isCanonicalPostSessionStateVerified,
  onEvent = () => {},
  setTimeoutFn = setTimeout,
  clearTimeoutFn = clearTimeout,
} = {}) {
  const delays = normalizePostCompletionReconciliationSchedule(schedule);
  let activeScope = null;
  let generation = 0;
  let timers = [];
  let completedAttempts = 0;

  const emit = (type, detail = {}) => {
    onEvent(type, {
      documentPath: activeScope
        ? postCompletionSessionDocumentPath(activeScope)
        : "",
      ...detail,
    });
  };

  const cancel = (reason = "cancelled") => {
    const wasActive = Boolean(activeScope);
    generation += 1;
    timers.forEach((timer) => clearTimeoutFn(timer));
    timers = [];
    if (wasActive) emit("cancelled", { reason });
    activeScope = null;
    return wasActive;
  };

  const finishIfExhausted = () => {
    if (!activeScope || completedAttempts < delays.length) return;
    emit("exhausted", { attempts: completedAttempts });
    cancel("attempt_limit_exhausted");
  };

  const attempt = async (attemptIndex, expectedGeneration) => {
    if (!activeScope || expectedGeneration !== generation) return;
    if (isVerified(getCurrentRecord())) {
      cancel("verified_before_read");
      return;
    }

    const attemptNumber = attemptIndex + 1;
    const scopeAtStart = activeScope;
    emit("read-start", {
      attempt: attemptNumber,
      maxAttempts: delays.length,
      scheduledDelayMs: delays[attemptIndex],
    });
    try {
      const record = await readRecord(scopeAtStart, {
        attempt: attemptNumber,
        maxAttempts: delays.length,
        scheduledDelayMs: delays[attemptIndex],
      });
      if (!activeScope || expectedGeneration !== generation) return;
      const validation = validatePostCompletionReconciliationRecord(
        record,
        scopeAtStart
      );
      if (!validation.valid) {
        emit("read-rejected", {
          attempt: attemptNumber,
          reason: validation.reason,
        });
        return;
      }
      const appliedRecord = applyRecord(record, {
        source: "server_reconciliation",
        attempt: attemptNumber,
      }) || record;
      emit("read-applied", {
        attempt: attemptNumber,
        verified: isVerified(appliedRecord),
      });
      if (isVerified(appliedRecord)) {
        cancel("verified_by_server_reconciliation");
      }
    } catch (error) {
      if (!activeScope || expectedGeneration !== generation) return;
      emit("read-error", {
        attempt: attemptNumber,
        code: String(error?.code || "unknown"),
      });
    } finally {
      if (expectedGeneration === generation) {
        completedAttempts += 1;
        finishIfExhausted();
      }
    }
  };

  const start = (scope = {}) => {
    if (activeScope) cancel("scope_replaced");
    const normalized = normalizePostCompletionReconciliationScope(scope);
    if (!normalized.authScopeKey || !normalized.orgId || !normalized.sessionId) {
      return false;
    }
    activeScope = normalized;
    completedAttempts = 0;
    if (isVerified(getCurrentRecord())) {
      emit("not-started", { reason: "already_verified" });
      activeScope = null;
      return false;
    }
    const expectedGeneration = generation;
    emit("started", {
      attempts: delays.length,
      scheduleMs: delays,
    });
    timers = delays.map((delay, index) => setTimeoutFn(
      () => attempt(index, expectedGeneration),
      delay
    ));
    return true;
  };

  const observeRecord = (record, source = "listener") => {
    if (
      !activeScope ||
      !validatePostCompletionReconciliationRecord(record, activeScope).valid ||
      !isVerified(record)
    ) {
      return false;
    }
    cancel(
      source === "listener"
        ? "verified_by_listener"
        : "verified_by_authoritative_record"
    );
    return true;
  };

  return {
    start,
    cancel,
    observeRecord,
    isActive: () => Boolean(activeScope),
    getAttemptLimit: () => delays.length,
  };
}
